import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

interface FieldProps {
  label: string;
  /** Hint copy shown below the input. Strings are most common; a
   *  ReactNode is allowed so callers can embed links or short
   *  inline elements. */
  hint?: ReactNode;
  error?: string;
  /** Optional trailing label adornment (e.g. an "optional" tag). */
  trailing?: ReactNode;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  trailing,
  children,
}: FieldProps) {
  return (
    <label className="block space-y-1.5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-foreground/85">{label}</span>
        {trailing}
      </div>
      {children}
      {error ? (
        <span className="block text-xs text-bad">{error}</span>
      ) : hint ? (
        <span className="block text-xs leading-relaxed text-foreground/55">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

const fieldStyles =
  "w-full rounded-md border border-line bg-background/40 px-3 py-2 " +
  "text-sm placeholder:text-foreground/30 transition " +
  "hover:border-line-strong focus-ring focus-visible:border-accent/60 " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...rest }, ref) => (
  <input
    ref={ref}
    type="text"
    className={cn(fieldStyles, className)}
    {...rest}
  />
));
TextInput.displayName = "TextInput";

export const NumberInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...rest }, ref) => (
  <input
    ref={ref}
    type="number"
    className={cn(fieldStyles, "tabular-nums", className)}
    {...rest}
  />
));
NumberInput.displayName = "NumberInput";

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...rest }, ref) => (
  <textarea ref={ref} className={cn(fieldStyles, "min-h-24", className)} {...rest} />
));
TextArea.displayName = "TextArea";

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...rest }, ref) => (
  <div className="relative">
    <select
      ref={ref}
      className={cn(fieldStyles, "appearance-none pr-9", className)}
      {...rest}
    >
      {children}
    </select>
    <svg
      className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 text-foreground/50"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden
    >
      <path d="m3 4.5 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
));
Select.displayName = "Select";

export const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...rest }, ref) => (
  <input
    ref={ref}
    type="checkbox"
    className={cn(
      "h-4 w-4 rounded border-line bg-background accent-accent focus-ring",
      className,
    )}
    {...rest}
  />
));
Checkbox.displayName = "Checkbox";

/**
 * Input wrapper with a leading icon slot. Shape:
 *
 *   <SearchInput
 *     value={q}
 *     onChange={...}
 *     placeholder="Filter library"
 *   />
 */
interface SearchInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Display the ⌘K hint on the right. */
  showHint?: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, showHint, ...rest }, ref) => (
    <div className="relative">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        aria-hidden
      >
        <circle cx={11} cy={11} r={7} />
        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
      </svg>
      <input
        ref={ref}
        type="text"
        className={cn(fieldStyles, "pl-9", showHint && "pr-16", className)}
        {...rest}
      />
      {showHint && (
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 inline-flex h-5 -translate-y-1/2 items-center gap-0.5 rounded border border-line bg-surface px-1.5 font-mono text-[10px] text-foreground/55">
          ⌘K
        </kbd>
      )}
    </div>
  ),
);
SearchInput.displayName = "SearchInput";
